import { useEffect, useState, useCallback, type DragEvent } from 'react'
import { useUIStore } from '@/shared/stores/ui.store'
import { trpc } from '@/shared/lib/trpc'
import { useAuthStore } from '@/shared/stores/auth.store'
import { dbgEvent } from '@/shared/lib/debug'
import { MessageList } from './MessageList'
import { TerminalView } from './TerminalView'
import { InputBar } from './InputBar'
import { StatusBar } from '@/widgets/StatusBar'
import { ErrorBoundary } from '@/shared/ui/ErrorBoundary'

// Interval heartbeat (ms) — doit être inférieur à la grace period daemon (3 min)
const HEARTBEAT_INTERVAL_MS = 60_000

export function ChatWidget({ sessionId }: { sessionId: string }) {
  const terminalMode = useUIStore(s => s.terminalMode)
  const accessToken = useAuthStore(s => s.accessToken)
  const [droppedFiles, setDroppedFiles] = useState<FileList | null>(null)
  const [messageSentSignal, setMessageSentSignal] = useState(0)

  const handleMessageSent = useCallback(() => {
    setMessageSentSignal(n => n + 1)
  }, [])

  // Lire createdAt depuis le cache React Query sessions.list (aucun fetch supplementaire)
  const { data: sessions } = trpc.sessions.list.useQuery(
    {},
    { enabled: !!accessToken },
  )
  const sessionData = sessions?.find(s => s.id === sessionId)
  const createdAt = sessionData?.created_at ?? null

  // Heartbeat : tant que ce composant est monté, signaler au daemon que le client est présent.
  // Reset la grace period de 3min pour éviter la fermeture du process SDK pendant la lecture.
  // Via mutation tRPC (le client a basculé sur Centrifugo — plus de socket /ws pour porter
  // `agent:heartbeat`). `mutate` est référentiellement stable (React Query).
  const heartbeatMutation = trpc.sessions.heartbeat.useMutation()
  useEffect(() => {
    const interval = setInterval(() => {
      heartbeatMutation.mutate({ sessionId })
    }, HEARTBEAT_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [sessionId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Intercepter le drag sur toute la zone chat pour éviter que le browser ouvre le fichier
  // Le drop est relayé vers InputBar via la prop droppedFiles
  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer.files.length > 0) {
      dbgEvent('Bus', 'emit files:drop', { sessionId, files: e.dataTransfer.files })
      setDroppedFiles(e.dataTransfer.files)
    }
  }, [sessionId])

  if (!sessionId) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-sm)',
        }}
      >
        Aucune conversation
      </div>
    )
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {terminalMode ? (
        <ErrorBoundary name="TerminalView">
          <TerminalView sessionId={sessionId} messageSentSignal={messageSentSignal} />
        </ErrorBoundary>
      ) : (
        <ErrorBoundary name="MessageList">
          <MessageList sessionId={sessionId} messageSentSignal={messageSentSignal} />
        </ErrorBoundary>
      )}
      <ErrorBoundary name="StatusBar">
        <StatusBar sessionId={sessionId} createdAt={createdAt} />
      </ErrorBoundary>
      <ErrorBoundary name="InputBar">
        <InputBar sessionId={sessionId} droppedFiles={droppedFiles} onMessageSent={handleMessageSent} />
      </ErrorBoundary>
    </div>
  )
}
